import { reflect } from '@effector/reflect';
import { Checkbox, TextInput, PasswordInput, Select } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { PenSquare } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
	USER_NAME_MAX_LENGTH,
	USER_NAME_MIN_LENGTH,
	USER_NOTE_MAX_LENGTH,
	USER_PASSWORD_MIN_LENGTH,
} from 'entities/user';
import { useForbiddenUserAction, viewerModel } from 'entities/viewer';
import { authModel, USER_ROLES, UserRole, UserWithPermission } from 'shared/api';
import { $t, translated } from 'shared/lib';
import { createModalForm, ModalFormActionIconProps, ModalFormProps } from 'shared/ui';
import { EditUserModel } from '../../model';

export type EditUserModalProps = ModalFormProps;

export const createEditUserModal = (model: EditUserModel) => {
	const ModalForm = createModalForm(model);

	const NameField = reflect({
		view: TextInput,
		bind: {
			'data-test-id': 'edit-user-name-input',
			label: translated('edit-user.name'),
			placeholder: translated('edit-user.name'),
			errorProps: { 'data-test-id': 'edit-user-name-input-error' },
			error: combine(
				[model.form.fields.name.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('edit-user.name'),
						minLength: USER_NAME_MIN_LENGTH,
						maxLength: USER_NAME_MAX_LENGTH,
					}),
			),
			value: model.form.fields.name.$value,
			onChange: (e) => model.form.fields.name.change(e.target.value),
		},
	});

	const PasswordField = reflect({
		view: PasswordInput,
		bind: {
			'data-test-id': 'edit-user-password-input',
			label: translated('edit-user.password'),
			placeholder: translated('edit-user.password'),
			errorProps: { 'data-test-id': 'edit-user-password-input-error' },
			error: combine(
				[model.form.fields.password.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('edit-user.password'),
						minLength: USER_PASSWORD_MIN_LENGTH,
					}),
			),
			value: model.form.fields.password.$value,
			onChange: (e) => model.form.fields.password.change(e.target.value),
		},
	});

	const RoleField = reflect({
		view: Select,
		bind: {
			'data-test-id': 'edit-user-role-select',
			label: translated('edit-user.role'),
			placeholder: translated('edit-user.role'),
			value: model.form.fields.role.$value.map((value) => value?.toString() ?? null),
			onChange: (value) =>
				model.form.fields.role.change(value === null ? value : (Number(value) as UserRole)),
			data: combine([$t, viewerModel.$accountId, authModel.$role, model.$modalData]).map(
				([t, accountId, role, modalData]) =>
					Object.values(USER_ROLES)
						.filter((r) => {
							const targetAccountId = modalData?.account.id ?? null;

							const isSameAccount = targetAccountId === accountId;
							const isGod = role === USER_ROLES.god;

							return isGod && isSameAccount ? true : r !== USER_ROLES.god;
						})
						.map((r) => ({
							value: `${r}`,
							label: t(`user-roles.${r}`),
						})),
			),
		},
	});

	const NoteField = reflect({
		view: TextInput,
		bind: {
			'data-test-id': 'edit-user-note-input',
			label: translated('edit-user.note'),
			placeholder: translated('edit-user.note'),
			errorProps: { 'data-test-id': 'edit-user-note-input-error' },
			error: combine(
				[model.form.fields.note.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('edit-user.note'),
						maxLength: USER_NOTE_MAX_LENGTH,
					}),
			),
			value: model.form.fields.note.$value,
			onChange: (e) => model.form.fields.note.change(e.target.value),
		},
	});

	const MultiLoginField = reflect({
		view: Checkbox,
		bind: {
			'data-test-id': 'edit-user-has-multi-login-checkbox',
			label: translated('edit-user.has-multi-login'),
			disabled: model.form.fields.hasMultiLogin.$disabled,
			checked: model.form.fields.hasMultiLogin.$value,
			onChange: (e) => model.form.fields.hasMultiLogin.change(e.target.checked),
		},
	});

	const EditUserModal = forwardRef<HTMLDivElement, EditUserModalProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const [pending, pristine] = useUnit([model.$pending, model.form.$pristine]);

		return (
			<ModalForm
				ref={ref}
				title={t('edit-user.title')}
				cancelButtonProps={{
					children: t('edit-user.cancel'),
				}}
				submitButtonProps={{
					children: t('edit-user.save'),
					loading: pending,
					disabled: pristine,
				}}
				{...props}
			>
				<NameField />
				<PasswordField />
				<RoleField />
				<NoteField />
				<MultiLoginField />
			</ModalForm>
		);
	});

	const EditUserModalActionIcon = forwardRef<
		HTMLButtonElement,
		ModalFormActionIconProps<UserWithPermission>
	>(({ modalData, children, onClick, ...props }, ref) => {
		const disabled = useForbiddenUserAction(modalData);

		return (
			<ModalForm.ActionIcon
				ref={ref}
				data-test-id='edit-user-action-icon'
				variant='ghost-secondary'
				disabled={disabled}
				modalData={modalData}
				{...props}
			>
				<PenSquare />
			</ModalForm.ActionIcon>
		);
	});

	return Object.assign(EditUserModal, {
		ActionIcon: EditUserModalActionIcon,
	});
};
